/**
 * @fileoverview Assignment Service
 * 
 * Service layer for knowledge base assignment operations including
 * API communication, caching, and business logic coordination.
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Assignment Service Class
 * 
 * Provides centralized assignment management with caching and error handling
 */
class AssignmentService {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.defaultCacheTTL = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Initialize service with API client and configuration
   * 
   * @param {Object} apiClient - API client instance
   * @param {KnowledgeBaseAssignmentConfig} config - Module configuration
   */
  initialize(apiClient, config) {
    this.apiClient = apiClient;
    this.config = config;
  }

  /**
   * Get assignments for files with caching
   * 
   * @param {string[]} fileIds - File IDs to get assignments for
   * @param {boolean} useCache - Whether to use cached data
   * @returns {Promise<Object>} Assignments grouped by file ID
   */
  async getAssignments(fileIds, useCache = true) {
    const cacheKey = `assignments:${fileIds.sort().join(',')}`;
    
    // Check cache first
    if (useCache && this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const response = await this.apiClient.post('/api/knowledge-base/assignments/batch', {
        fileIds,
        context: this.config.context
      });

      const assignments = {};
      (response.assignments || []).forEach(assignment => {
        if (!assignments[assignment.fileId]) {
          assignments[assignment.fileId] = [];
        }
        assignments[assignment.fileId].push(assignment);
      });

      // Cache the result
      this.setCache(cacheKey, assignments);
      
      return assignments;

    } catch (error) {
      console.error('Failed to get assignments:', error);
      throw error;
    }
  }

  /**
   * Create new assignment
   * 
   * @param {string} fileId - File ID
   * @param {string} parrotId - Parrot ID
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Created assignment
   */
  async createAssignment(fileId, parrotId, options = {}) {
    try {
      const response = await this.apiClient.post('/api/knowledge-base/assignments', {
        fileId,
        parrotId,
        context: this.config.context,
        ...options
      });

      // Invalidate related cache entries
      this.invalidateAssignmentCache(fileId);
      
      return response.assignment;

    } catch (error) {
      console.error('Failed to create assignment:', error);
      throw error;
    }
  }

  /**
   * Delete assignment
   * 
   * @param {string} fileId - File ID
   * @param {string} parrotId - Parrot ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteAssignment(fileId, parrotId) {
    try {
      await this.apiClient.delete(`/api/knowledge-base/assignments/${fileId}/${parrotId}`);

      // Invalidate related cache entries
      this.invalidateAssignmentCache(fileId);
      
      return true;

    } catch (error) {
      console.error('Failed to delete assignment:', error);
      throw error;
    }
  }

  /**
   * Create batch assignment operation
   * 
   * @param {string} operation - Operation type (assign, unassign, reassign)
   * @param {string[]} fileIds - File IDs
   * @param {string[]} parrotIds - Parrot IDs
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Batch operation result
   */
  async createBatchAssignment(operation, fileIds, parrotIds, options = {}) {
    try {
      const response = await this.apiClient.post('/api/knowledge-base/assignments/batch', {
        operation,
        fileIds,
        parrotIds,
        context: this.config.context,
        ...options
      });

      // Invalidate cache for all affected files
      fileIds.forEach(fileId => this.invalidateAssignmentCache(fileId));
      
      return response.batchOperation;

    } catch (error) {
      console.error('Failed to create batch assignment:', error);
      throw error;
    }
  }

  /**
   * Get batch operation status
   * 
   * @param {string} batchId - Batch operation ID
   * @returns {Promise<Object>} Batch operation status
   */
  async getBatchOperationStatus(batchId) {
    try {
      const response = await this.apiClient.get(`/api/knowledge-base/assignments/batch/${batchId}`);
      return response.batchOperation;

    } catch (error) {
      console.error('Failed to get batch operation status:', error);
      throw error;
    }
  }

  /**
   * Check for assignment conflicts
   * 
   * @param {Object[]} operations - Operations to check
   * @returns {Promise<Object[]>} Detected conflicts
   */
  async checkConflicts(operations) {
    try {
      const response = await this.apiClient.post('/api/knowledge-base/assignments/check-conflicts', {
        operations,
        context: this.config.context
      });

      return response.conflicts || [];

    } catch (error) {
      console.error('Failed to check conflicts:', error);
      return [];
    }
  }

  /**
   * Resolve assignment conflicts
   * 
   * @param {Object[]} conflicts - Conflicts to resolve
   * @param {Object} resolutions - Resolution strategies
   * @returns {Promise<Object[]>} Resolution results
   */
  async resolveConflicts(conflicts, resolutions) {
    try {
      const response = await this.apiClient.post('/api/knowledge-base/assignments/resolve-conflicts', {
        conflicts,
        resolutions,
        context: this.config.context
      });

      // Invalidate cache for affected files
      conflicts.forEach(conflict => {
        if (conflict.fileId) {
          this.invalidateAssignmentCache(conflict.fileId);
        }
      });

      return response.results || [];

    } catch (error) {
      console.error('Failed to resolve conflicts:', error);
      throw error;
    }
  }

  /**
   * Get assignment history
   * 
   * @param {Object} filters - History filters
   * @returns {Promise<Object[]>} Assignment history
   */
  async getAssignmentHistory(filters = {}) {
    try {
      const queryParams = new URLSearchParams({
        context: this.config.context,
        ...filters
      });

      const response = await this.apiClient.get(`/api/knowledge-base/assignments/history?${queryParams}`);
      return response.history || [];

    } catch (error) {
      console.error('Failed to get assignment history:', error);
      return [];
    }
  }

  /**
   * Get sync status for assignments
   * 
   * @param {string[]} fileIds - File IDs to get sync status for
   * @returns {Promise<Object>} Sync statuses grouped by file and parrot
   */
  async getSyncStatuses(fileIds) {
    const cacheKey = `sync-status:${fileIds.sort().join(',')}`;
    
    // Check cache first (shorter TTL for sync status)
    if (this.isCacheValid(cacheKey, 30000)) { // 30 seconds
      return this.cache.get(cacheKey);
    }

    try {
      const response = await this.apiClient.post('/api/knowledge-base/sync-status/batch', {
        fileIds,
        context: this.config.context
      });

      const syncStatuses = {};
      (response.statuses || []).forEach(status => {
        if (!syncStatuses[status.fileId]) {
          syncStatuses[status.fileId] = {};
        }
        syncStatuses[status.fileId][status.parrotId] = status;
      });

      // Cache with shorter TTL
      this.setCache(cacheKey, syncStatuses, 30000);
      
      return syncStatuses;

    } catch (error) {
      console.error('Failed to get sync statuses:', error);
      return {};
    }
  }

  /**
   * Trigger sync for assignment
   * 
   * @param {string} fileId - File ID
   * @param {string} parrotId - Parrot ID
   * @returns {Promise<Object>} Sync operation result
   */
  async triggerSync(fileId, parrotId) {
    try {
      const response = await this.apiClient.post('/api/knowledge-base/sync/trigger', {
        fileId,
        parrotId,
        context: this.config.context
      });

      // Invalidate sync status cache
      this.invalidateSyncStatusCache(fileId);
      
      return response.syncOperation;

    } catch (error) {
      console.error('Failed to trigger sync:', error);
      throw error;
    }
  }

  /**
   * Get assignment statistics
   * 
   * @param {Object} filters - Statistics filters
   * @returns {Promise<Object>} Assignment statistics
   */
  async getAssignmentStatistics(filters = {}) {
    try {
      const queryParams = new URLSearchParams({
        context: this.config.context,
        ...filters
      });

      const response = await this.apiClient.get(`/api/knowledge-base/assignments/statistics?${queryParams}`);
      return response.statistics || {};

    } catch (error) {
      console.error('Failed to get assignment statistics:', error);
      return {};
    }
  }

  /**
   * Validate assignment before creation
   * 
   * @param {string} fileId - File ID
   * @param {string} parrotId - Parrot ID
   * @param {Object} options - Validation options
   * @returns {Promise<Object>} Validation result
   */
  async validateAssignment(fileId, parrotId, options = {}) {
    try {
      const response = await this.apiClient.post('/api/knowledge-base/assignments/validate', {
        fileId,
        parrotId,
        context: this.config.context,
        ...options
      });

      return response.validation;

    } catch (error) {
      console.error('Failed to validate assignment:', error);
      return { valid: false, errors: [error.message] };
    }
  }

  /**
   * Check if cache entry is valid
   * 
   * @param {string} key - Cache key
   * @param {number} customTTL - Custom TTL in milliseconds
   * @returns {boolean} Whether cache is valid
   */
  isCacheValid(key, customTTL = null) {
    if (!this.cache.has(key)) {
      return false;
    }

    const expiry = this.cacheExpiry.get(key);
    const ttl = customTTL || this.defaultCacheTTL;
    
    return expiry && (Date.now() - expiry) < ttl;
  }

  /**
   * Set cache entry with expiry
   * 
   * @param {string} key - Cache key
   * @param {*} value - Value to cache
   * @param {number} customTTL - Custom TTL in milliseconds
   */
  setCache(key, value, customTTL = null) {
    this.cache.set(key, value);
    this.cacheExpiry.set(key, Date.now());
  }

  /**
   * Invalidate assignment cache for file
   * 
   * @param {string} fileId - File ID
   */
  invalidateAssignmentCache(fileId) {
    // Remove all cache entries that include this file ID
    for (const [key] of this.cache) {
      if (key.includes(`assignments:`) && key.includes(fileId)) {
        this.cache.delete(key);
        this.cacheExpiry.delete(key);
      }
    }
  }

  /**
   * Invalidate sync status cache for file
   * 
   * @param {string} fileId - File ID
   */
  invalidateSyncStatusCache(fileId) {
    // Remove all sync status cache entries that include this file ID
    for (const [key] of this.cache) {
      if (key.includes(`sync-status:`) && key.includes(fileId)) {
        this.cache.delete(key);
        this.cacheExpiry.delete(key);
      }
    }
  }

  /**
   * Clear all cache
   */
  clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  /**
   * Get cache statistics
   * 
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const [key, expiry] of this.cacheExpiry) {
      if ((now - expiry) < this.defaultCacheTTL) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      hitRate: this.hitRate || 0
    };
  }
}

// Create singleton instance
export const assignmentService = new AssignmentService();

export default assignmentService;
